"use client";

import { useMarketingAnalytics } from "@analytics";
import { cn } from "@repo/ui";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

function ChevronUpIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			viewBox="0 0 24 24"
			aria-hidden="true"
			width={24}
			height={24}
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M19 15l-7-7-7 7"
			/>
		</svg>
	);
}

export function LandingBackToTop() {
	const t = useTranslations("marketing.landing");
	const { landingBackToTopClicked } = useMarketingAnalytics();
	const [visible, setVisible] = useState(false);
	const [bottomPx, setBottomPx] = useState(24);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		function onScroll() {
			setVisible(window.scrollY > 0);
		}
		window.addEventListener("scroll", onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	const recomputeBottom = useCallback(() => {
		const separator = document.querySelector<HTMLElement>(
			"[data-footer-separator]",
		);
		if (!separator) {
			setBottomPx(24);
			return;
		}
		const rect = separator.getBoundingClientRect();
		/** Use the separator top (not center) so lift starts as soon as the footer band enters view. */
		const anchorY = rect.top;
		/** Extra px so the fixed button sits a little higher above the footer band. */
		const extraClearancePx = 23;
		const distanceFromViewportBottom = Math.max(
			0,
			window.innerHeight - anchorY + extraClearancePx,
		);
		// Keep the button from dropping below the footer separator band.
		setBottomPx(Math.max(24, Math.round(distanceFromViewportBottom)));
	}, []);

	useEffect(() => {
		const schedule = () => {
			if (rafRef.current != null) {
				return;
			}
			rafRef.current = window.requestAnimationFrame(() => {
				rafRef.current = null;
				recomputeBottom();
			});
		};

		window.addEventListener("scroll", schedule, { passive: true });
		window.addEventListener("resize", schedule, { passive: true });
		schedule();
		return () => {
			window.removeEventListener("scroll", schedule);
			window.removeEventListener("resize", schedule);
			if (rafRef.current != null) {
				window.cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [recomputeBottom]);

	function scrollToTop() {
		landingBackToTopClicked();
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	return (
		<button
			type="button"
			aria-label={t("backToTopAria")}
			aria-hidden={!visible}
			tabIndex={visible ? 0 : -1}
			onClick={scrollToTop}
			className={cn(
				"group fixed right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-lg transition-opacity duration-300 hover:bg-slate-50 active:bg-slate-100",
				"outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
				visible
					? "pointer-events-auto opacity-100"
					: "pointer-events-none opacity-0",
			)}
			style={{ bottom: bottomPx }}
		>
			<ChevronUpIcon className="h-6 w-6 text-slate-600 transition-colors group-hover:text-slate-900" />
		</button>
	);
}
